import { Component } from '@angular/core';
import { ModalRef } from 'asksuite-citrus';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import moment from 'moment';
import { environment } from '@env';
import { CompanyBalance, CreditsBalanceService } from '@core/services/credits-balance.service';
import { UserService } from '@core/services/user.service';
import {
  countCreditsV2Filters,
  CreditsV2Severity,
  filterCreditsV2,
} from '@core/utils/credits-v2.utils';

export const CREDITS_V2_DISCLAIMER_STORAGE_KEY = 'livechat:credits-v2-disclaimer-dismissed';
/** With a single company there is nothing to filter. */
const CREDITS_V2_FILTERS_MIN_COMPANIES = 2;

/** WhatsApp credits modal v2 (toggle `enable_whatsapp_credits_v2`). */
@Component({
  selector: 'app-credits-balance-modal-v2',
  templateUrl: './credits-balance-modal-v2.component.html',
  styleUrls: ['./credits-balance-modal-v2.component.scss'],
})
export class CreditsBalanceModalV2Component {
  protected readonly severities: { value: CreditsV2Severity; labelKey: string; icon?: string }[] = [
    { value: 'critical', labelKey: 'CREDITS_BALANCE_V2.FILTER_CRITICAL', icon: 'dangerous' },
    { value: 'warning', labelKey: 'CREDITS_BALANCE_V2.FILTER_WARNING', icon: 'warning' },
    { value: 'normal', labelKey: 'CREDITS_BALANCE_V2.FILTER_NORMAL' },
  ];
  private readonly severity$ = new BehaviorSubject<CreditsV2Severity | null>(null);
  private readonly bonus$ = new BehaviorSubject<boolean>(false);
  private readonly search$ = new BehaviorSubject<string>('');

  /** Filters start clean on every open: the component lives as long as the modal. */
  protected readonly view$ = combineLatest([
    this.creditsBalanceService.balances$,
    this.severity$,
    this.bonus$,
    this.search$,
  ]).pipe(
    map(([balances, severity, bonus, search]) => {
      const showFilters = balances.length >= CREDITS_V2_FILTERS_MIN_COMPANIES;
      return {
        showFilters,
        severity,
        bonus,
        search,
        counts: countCreditsV2Filters(balances),
        balances: showFilters ? filterCreditsV2(balances, { severity, bonus, search }) : balances,
      };
    })
  );
  protected readonly loading$ = this.creditsBalanceService.loading$;
  protected readonly lastUpdated$ = this.creditsBalanceService.lastUpdatedAt$.pipe(
    map((date) => (date ? moment(date).format('HH[h] mm[min]') : null))
  );

  /** Buying and notifications live in the admin, which only managers can open. */
  protected readonly canManageCredits =
    this.userService.isCurrentUserManager() || this.userService.isCurrentUserSuperAdmin();
  protected readonly creditsStatementUrl = `${environment.externalUrls.platform}/whatsapp/credits-statement`;
  protected readonly notificationsUrl = `${environment.externalUrls.platform}/whatsapp/notifications`;

  protected showDisclaimer = !this.readDisclaimerDismissed();

  constructor(
    private modalRef: ModalRef,
    private creditsBalanceService: CreditsBalanceService,
    private userService: UserService
  ) {}

  /** Copy of the red "Crítico" strip: empty with bonus is only blocked once the bonus ends. */
  protected statusMessageKey(balance: CompanyBalance): string {
    if (balance.level !== 3) return 'CREDITS_BALANCE_V2.CRITICAL_MESSAGE';
    return balance.bonus ? 'CREDITS_BALANCE_V2.EMPTY_BONUS_MESSAGE' : 'CREDITS_BALANCE_V2.EMPTY_MESSAGE';
  }

  /** Text under the bar inside the Meta bonus: free messages while healthy, a heads-up once credits run low. */
  protected bonusMessageKey(balance: CompanyBalance): string {
    if (balance.level === 0) return 'CREDITS_BALANCE_V2.FROZEN_MESSAGE';
    if (balance.level === 3) return 'CREDITS_BALANCE_V2.EMPTY_BONUS_TEXT';
    return 'CREDITS_BALANCE_V2.BONUS_ACTIVE_MESSAGE';
  }

  /** Radio that can be unselected: clicking the active pill shows every severity again. */
  toggleSeverity(severity: CreditsV2Severity): void {
    this.severity$.next(this.severity$.value === severity ? null : severity);
  }

  toggleBonus(): void {
    this.bonus$.next(!this.bonus$.value);
  }

  setSearch(term: string): void {
    this.search$.next(term);
  }

  closeModal(): void {
    this.modalRef.close();
  }

  /** Hidden until the end of the browser session. */
  dismissDisclaimer(): void {
    this.showDisclaimer = false;
    try {
      sessionStorage.setItem(CREDITS_V2_DISCLAIMER_STORAGE_KEY, '1');
    } catch {
      // Storage blocked: the disclaimer just comes back on the next open.
    }
  }

  private readDisclaimerDismissed(): boolean {
    try {
      return sessionStorage.getItem(CREDITS_V2_DISCLAIMER_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}
